/**
 * Saving a backup to the operator's own Google Drive.
 *
 * The whole flow happens in the browser, and that is the point. The operator
 * signs in to their own Google account, Google hands *this page* a token that
 * lasts an hour, and the archive goes from the browser straight to Drive. Our
 * server never talks to Google, never sees the token, and holds no refresh
 * token — so there is nothing here for anyone to steal later, and no standing
 * access to somebody's Drive sitting in our database.
 *
 * The scope is `drive.file`, which is the narrow one: this application can see
 * and touch only the files it created itself. It cannot read the rest of their
 * Drive, and Google does not put that scope through its verification review —
 * the same reason sign-in asks only for openid, email and profile.
 *
 * What this cannot do is run unattended. A token that lives in a browser tab
 * dies with it, so a nightly backup to Drive would need a refresh token stored
 * on the server, which is a different feature with a different risk to weigh.
 */

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const FOLDER_NAME = 'FlyPack CAMO backups';
/** The folder's name before the rename. Found and renamed, never left beside a new one. */
const OLD_FOLDER_NAME = 'AirSight backups';
const FOLDER_MIME = 'application/vnd.google-apps.folder';

interface TokenResponse {
  access_token?: string;
  error?: string;
  error_description?: string;
}

interface TokenClient {
  requestAccessToken: (overrides?: { prompt?: string }) => void;
}

interface GoogleIdentityServices {
  accounts: {
    oauth2: {
      initTokenClient: (config: {
        client_id: string;
        scope: string;
        callback: (response: TokenResponse) => void;
        error_callback?: (error: { type?: string; message?: string }) => void;
      }) => TokenClient;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

let loading: Promise<GoogleIdentityServices> | null = null;

/** Google's own script, fetched only when somebody actually asks for Drive. */
function loadGoogleIdentity(): Promise<GoogleIdentityServices> {
  if (window.google?.accounts?.oauth2) return Promise.resolve(window.google);
  if (loading) return loading;

  loading = new Promise<GoogleIdentityServices>((resolve, reject) => {
    const el = document.createElement('script');
    el.src = GIS_SRC;
    el.async = true;
    el.onload = () => {
      if (window.google?.accounts?.oauth2) resolve(window.google);
      else reject(new Error('Google loaded, but not the part that asks for permission.'));
    };
    el.onerror = () => {
      loading = null;
      reject(new Error('Could not reach Google. Check the connection and try again.'));
    };
    document.head.appendChild(el);
  });
  return loading;
}

/**
 * Fetch Google's script ahead of the click that needs it.
 *
 * Called when the card renders. Failure is silent on purpose: nobody has asked
 * for anything yet, and the click will report it properly if it comes.
 */
export function preloadGoogleIdentity(): void {
  void loadGoogleIdentity().catch(() => undefined);
}

/**
 * One hour of permission, asked for out loud.
 *
 * A popup, so it must be called straight out of a click — a browser that has
 * lost the trail from a gesture to this call will block it, and the operator
 * sees nothing at all happen.
 */
export async function askForDrive(clientId: string): Promise<string> {
  const gis = await loadGoogleIdentity();

  return new Promise<string>((resolve, reject) => {
    const client = gis.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: DRIVE_SCOPE,
      callback: (response) => {
        if (response.access_token) resolve(response.access_token);
        else reject(new Error(response.error_description || response.error || 'Google did not grant access.'));
      },
      // Google's own wording here is "Failed to open popup window", which
      // tells an operator nothing about what to do next — and a blocked popup
      // is by far the most likely way this goes wrong.
      error_callback: (error) => {
        const blocked = error.type === 'popup_failed_to_open' || /popup/i.test(error.message ?? '');
        reject(
          new Error(
            error.type === 'popup_closed'
              ? 'The Google window was closed before it finished. Nothing was saved.'
              : blocked
                ? 'Your browser blocked the Google window. Allow pop-ups for this site, then try again.'
                : error.message || 'Google would not open its permission window.',
          ),
        );
      },
    });
    client.requestAccessToken();
  });
}

async function drive<T>(token: string, url: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { authorization: `Bearer ${token}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(payload?.error?.message ?? `Google Drive refused that (${res.status}).`);
  }
  return (await res.json()) as T;
}

/**
 * A folder to put them in, so a year of backups is a shelf and not a scatter.
 *
 * The search only ever sees folders this application created — that is what
 * `drive.file` means — so it cannot collide with a folder of the operator's
 * own. If they delete ours, the next save quietly makes another.
 */
async function backupFolder(token: string): Promise<{ id: string | null; why: string | null }> {
  const query = `(name='${FOLDER_NAME}' or name='${OLD_FOLDER_NAME}') and mimeType='${FOLDER_MIME}' and trashed=false`;
  const found = await drive<{ files?: Array<{ id: string; name: string }> }>(
    token,
    `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name)&spaces=drive`,
  ).catch((e: Error) => e);
  if (!(found instanceof Error) && found.files?.length) {
    const folder = found.files.find((f) => f.name === FOLDER_NAME) ?? found.files[0];
    // Made before the rename: give it the new name. If that fails the backup
    // still goes into it, which is what matters.
    if (folder.name !== FOLDER_NAME) {
      await drive(token, `https://www.googleapis.com/drive/v3/files/${folder.id}?fields=id`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name: FOLDER_NAME }),
      }).catch(() => undefined);
    }
    return { id: folder.id, why: null };
  }

  const made = await drive<{ id: string }>(token, 'https://www.googleapis.com/drive/v3/files?fields=id', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ name: FOLDER_NAME, mimeType: FOLDER_MIME }),
  }).catch((e: Error) => e);

  // A backup in the root of their Drive is worth more than no backup at all —
  // but the reason it went there is kept, because the first version threw the
  // reason away and then told the operator it was in a folder that had never
  // been created. Somebody went looking for that folder.
  if (made instanceof Error) return { id: null, why: made.message };
  return { id: made.id, why: null };
}

export interface DriveUpload {
  id: string;
  name: string;
  link: string | null;
  /** Where it actually landed — null means the root of their Drive. */
  folder: string | null;
  /** Why it is not in the folder, when it is not. */
  folderProblem: string | null;
}

/**
 * The upload itself, resumable and reported.
 *
 * Resumable rather than a single multipart POST because a busy operator's
 * archive is not small — the documents dominate — and a plain upload that
 * fails at 90% on a hotel connection fails all the way back to nothing. It
 * also means the browser can say how far it has got, which for a file this
 * size is the difference between waiting and wondering.
 */
export async function uploadToDrive(
  token: string,
  fileName: string,
  body: Blob,
  onProgress?: (fraction: number) => void,
): Promise<DriveUpload> {
  const folder = await backupFolder(token);
  const metadata = {
    name: fileName,
    mimeType: 'application/zip',
    description: 'FlyPack CAMO — continuing airworthiness records, exported from the live system.',
    ...(folder.id ? { parents: [folder.id] } : {}),
  };

  const start = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=resumable', {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
      'x-upload-content-type': 'application/zip',
      'x-upload-content-length': String(body.size),
    },
    body: JSON.stringify(metadata),
  });
  if (!start.ok) {
    const payload = (await start.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(payload?.error?.message ?? `Google Drive would not start the upload (${start.status}).`);
  }

  /**
   * Where to send the bytes — if the browser is allowed to read it.
   *
   * A resumable upload answers with the session address in a `Location`
   * header, and a header is only legible to script when the server lists it in
   * `Access-Control-Expose-Headers`. That is not ours to guarantee, and an
   * upload that cannot start is worth less than one that cannot resume — so
   * when the header is unreadable this falls back to sending the whole thing
   * in one request, which needs no header at all.
   */
  const session = start.headers.get('location');
  const uploaded = session
    ? await send('PUT', session, body, 'application/zip', onProgress)
    : await send('POST', multipartUrl(), multipartBody(metadata, body), `multipart/related; boundary=${BOUNDARY}`, onProgress, token);

  const link = await drive<{ webViewLink?: string }>(
    token,
    `https://www.googleapis.com/drive/v3/files/${uploaded.id}?fields=webViewLink`,
  ).catch(() => null);

  return {
    id: uploaded.id,
    name: uploaded.name ?? fileName,
    link: link?.webViewLink ?? null,
    folder: folder.id ? FOLDER_NAME : null,
    folderProblem: folder.why,
  };
}

const BOUNDARY = 'airsight-boundary-6f1a2c';

const multipartUrl = () =>
  'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name';

/** Metadata and bytes in one body, which is what a multipart upload is. */
function multipartBody(metadata: object, body: Blob): Blob {
  return new Blob([
    `--${BOUNDARY}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n`,
    JSON.stringify(metadata),
    `\r\n--${BOUNDARY}\r\nContent-Type: application/zip\r\n\r\n`,
    body,
    `\r\n--${BOUNDARY}--\r\n`,
  ]);
}

/**
 * The request that carries the bytes.
 *
 * XHR rather than fetch: fetch cannot say how much of a body has gone, and for
 * a file this size a silent minute reads as a hang.
 */
function send(
  method: 'PUT' | 'POST',
  url: string,
  body: Blob,
  contentType: string,
  onProgress?: (fraction: number) => void,
  token?: string,
): Promise<{ id: string; name?: string }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    xhr.setRequestHeader('content-type', contentType);
    // The resumable session address carries its own authorisation; a fresh
    // request to the upload endpoint does not.
    if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          reject(new Error('Google Drive answered with something unreadable.'));
        }
        return;
      }
      let said = '';
      try {
        said = (JSON.parse(xhr.responseText) as { error?: { message?: string } }).error?.message ?? '';
      } catch {
        said = '';
      }
      reject(new Error(said || `Google Drive refused the file (${xhr.status}).`));
    };
    xhr.onerror = () => reject(new Error('The upload to Google Drive was interrupted.'));
    xhr.send(body);
  });
}
